import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { ReportHead, Empty } from '../components/Shell.jsx';

/**
 * Where stock may sit. Code, name, one default.
 *
 * The shape ChartOfAccounts.jsx already uses, and deliberately no more than
 * that. THERE IS NO EDIT AND NO DELETE HERE because the API has neither — the
 * only location routes are a list and a create. A rename button that answered
 * 404 would be worse than an absent one, so the screen offers what exists.
 *
 * A location cannot be deleted for the same reason an account carrying a
 * posting cannot: movements name it, and a valuation that lost its locations
 * would stop being able to say where anything is.
 */
export function StockLocations({ entity }) {
  const [locations, setLocations] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', isDefault: false });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/api/entities/${entity.id}/stock-locations`)
    .then((d) => setLocations(d.locations))
    .catch((e) => { setErr(e.message); setLocations([]); });

  useEffect(() => { if (entity?.id) load(); /* eslint-disable-next-line */ }, [entity?.id]);

  const add = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.post(`/api/entities/${entity.id}/stock-locations`, {
        code: form.code.trim(), name: form.name.trim(), isDefault: form.isDefault,
      });
      setMsg(`Location ${form.code.trim()} added.`);
      setForm({ code: '', name: '', isDefault: false });
      setAdding(false);
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const existingDefault = (locations ?? []).find((l) => l.isDefault);

  return (
    <>
      <ReportHead entity={entity} title="Stock locations"
                  sub="The rooms, vans and shelves this company's stock can sit in." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <div className="note">
        A count sheet is opened on <strong>one location</strong>, and only one sheet may be open
        on a location at a time — two sheets on one room both freeze the same expected figures,
        and posting both writes the same difference off twice. An adjustment names one location
        too, because stock damaged in two store rooms is two events.
      </div>

      {adding ? (
        <div style={{ margin: '18px 0' }}>
          <div className="form-grid">
            <label className="field"><span>Code</span>
              <input className="input--figure" value={form.code} placeholder="MAIN"
                     onChange={(e) => setForm({ ...form, code: e.target.value })} /></label>
            <label className="field"><span>Name</span>
              <input value={form.name} placeholder="Main store"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="field" style={{ justifyContent: 'flex-end' }}>
              <span>Default</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={form.isDefault}
                       onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} />
                Use this one unless another is chosen
              </label>
            </label>
          </div>
          {form.isDefault && existingDefault && (
            <div className="note note--warn">
              {existingDefault.code} — {existingDefault.name} is the default today. Adding this one
              as the default moves it.
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <button className="btn" onClick={add}
                    disabled={busy || !form.code.trim() || form.name.trim().length < 2}>
              {busy ? 'Adding' : 'Add location'}
            </button>
            <button className="btn btn--quiet" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn" style={{ margin: '18px 0' }} onClick={() => setAdding(true)}>
          Add a location
        </button>
      )}

      {locations === null ? (
        <div className="empty">Loading…</div>
      ) : locations.length === 0 ? (
        <Empty>
          <p>No stock locations yet.</p>
          <p>Add the store room before taking stock on or counting it.</p>
        </Empty>
      ) : (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Code</th>
                <th>Location</th>
                <th style={{ width: 140 }} />
              </tr>
            </thead>
            <tbody>
              {locations.map((l) => (
                <tr key={l.id} style={{ opacity: l.isActive ? 1 : 0.5 }}>
                  <td className="code figure">{l.code}</td>
                  <td>
                    {l.name}
                    {!l.isActive && <span className="neg"> · inactive</span>}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                    {l.isDefault ? 'Default' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
